/**
 * Bringing this device and the server together: a three-way merge per key, decided from what
 * each side changed since they last agreed (the `base` fingerprints), never from clocks alone.
 *
 * THE CONFLICT RULE. Settings and key bindings are each their own thing: if only one side
 * changed one, that side's value is kept; if both did, this device's is (it is the one in the
 * player's hands). The CAMPAIGN - the profile and the mission suspended inside it - is one unit.
 * If only one side changed it, that side's is kept. If both did, the two profiles are compared
 * and the winner's campaign is taken whole:
 *   1. more missions flown wins (operation and classic attempts, plus Daily Drops on the table);
 *   2. if equal, more rangers ever enlisted wins (`nextRangerId`);
 *   3. if equal, the later write wins (this device's clock at its last unsent change against the
 *      server's clock at its last write);
 *   4. if still equal, the server's - so two devices in the same position reach the same answer.
 * The losing campaign is never destroyed: the caller keeps it under `nightdrop.backup.<time>`.
 */

import { fingerprint } from './state';
import type { CloudKey, SaveDocument } from './types';
import { CAMPAIGN_KEYS, CLOUD_KEY, CLOUD_KEYS } from './types';

export interface MergeInput {
  /** This device's value of each key; null when it has none. */
  local: Readonly<Record<CloudKey, string | null>>;
  dirty: ReadonlySet<CloudKey>;
  base: Readonly<Partial<Record<CloudKey, string>>>;
  remote: SaveDocument;
  /** This device's clock at its last unsent change. */
  dirtyAt: number | null;
}

export interface MergePlan {
  /** The server's values to write here. */
  take: Partial<Record<CloudKey, string>>;
  /** What still has to be sent once the plan is applied. */
  dirty: CloudKey[];
  /** Keys both sides now agree on: their base becomes the fingerprint of that value. */
  agreed: Partial<Record<CloudKey, string>>;
  /** The values that lost: this device's when the server won, the server's when this device did. */
  backup: Partial<Record<CloudKey, string>>;
  /** Set when both sides had changed the campaign. */
  conflict: { winner: 'local' | 'remote'; why: string } | null;
  /** The server's campaign differs from what this device last agreed to (whoever wins). */
  remoteCampaignChanged: boolean;
}

interface Progress {
  missions: number;
  rangers: number;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

/** How far a profile has come. An unreadable one counts as less than a day-one profile. */
export function campaignProgress(profileJson: string | null | undefined): Progress {
  let raw: unknown = null;
  try {
    raw = JSON.parse(profileJson ?? '');
  } catch {
    raw = null;
  }
  if (typeof raw !== 'object' || raw === null) return { missions: -1, rangers: -1 };
  const p = raw as Record<string, unknown>;
  const stats = typeof p['archetypeStats'] === 'object' && p['archetypeStats'] !== null ? p['archetypeStats'] : {};
  let missions = Array.isArray(p['daily']) ? p['daily'].length : 0;
  for (const entry of Object.values(stats)) {
    if (typeof entry === 'object' && entry !== null) missions += count((entry as Record<string, unknown>)['attempts']);
  }
  return { missions, rangers: count(p['nextRangerId']) };
}

/** Steps 1-4 of the rule above. */
export function campaignWinner(
  localProfile: string | null, remoteProfile: string | undefined, dirtyAt: number | null, remoteUpdatedAt: number,
): { winner: 'local' | 'remote'; why: string } {
  const mine = campaignProgress(localProfile);
  const theirs = campaignProgress(remoteProfile);
  if (mine.missions !== theirs.missions) {
    return { winner: mine.missions > theirs.missions ? 'local' : 'remote', why: 'more missions flown' };
  }
  if (mine.rangers !== theirs.rangers) {
    return { winner: mine.rangers > theirs.rangers ? 'local' : 'remote', why: 'more rangers enlisted' };
  }
  if (dirtyAt !== null && dirtyAt > remoteUpdatedAt) return { winner: 'local', why: 'saved later' };
  const level = dirtyAt === null || dirtyAt === remoteUpdatedAt;
  return { winner: 'remote', why: level ? 'level: the server copy is kept' : 'saved later' };
}

export function planMerge(input: MergeInput): MergePlan {
  const { local, base, remote } = input;
  const dirty = new Set(input.dirty);
  const plan: MergePlan = { take: {}, dirty: [], agreed: {}, backup: {}, conflict: null, remoteCampaignChanged: false };

  const same = (key: CloudKey): boolean => remote.keys[key] !== undefined && remote.keys[key] === (local[key] ?? '');
  /** The server's copy moved since this device last agreed with it. A key the server never had has not. */
  const remoteChanged = (key: CloudKey): boolean =>
    remote.keys[key] !== undefined && !same(key) && fingerprint(remote.keys[key]) !== base[key];

  for (const key of CLOUD_KEYS) {
    if (!same(key)) continue;
    // Nothing to send, nothing to take - the usual case after a flush whose answer this device never saw.
    dirty.delete(key);
    plan.agreed[key] = fingerprint(remote.keys[key]);
  }

  const takeRemote = (key: CloudKey): void => {
    const value = remote.keys[key];
    if (value === undefined) return;
    plan.take[key] = value;
    plan.agreed[key] = fingerprint(value);
    dirty.delete(key);
  };

  for (const key of CLOUD_KEYS) {
    if (CAMPAIGN_KEYS.includes(key) || !remoteChanged(key)) continue;
    // Both changed a preference: the device in the player's hands wins, and it is sent.
    if (!dirty.has(key)) takeRemote(key);
  }

  const localCampaign = CAMPAIGN_KEYS.some((key) => dirty.has(key));
  plan.remoteCampaignChanged = CAMPAIGN_KEYS.some(remoteChanged);
  if (plan.remoteCampaignChanged && !localCampaign) {
    for (const key of CAMPAIGN_KEYS) if (remoteChanged(key)) takeRemote(key);
    // The server's campaign has no suspended mission at all: this device's must not outlive its profile.
    if (remote.keys[CLOUD_KEY.suspend] === undefined && (local[CLOUD_KEY.suspend] ?? '') !== '') {
      plan.take[CLOUD_KEY.suspend] = '';
    }
  } else if (plan.remoteCampaignChanged && localCampaign) {
    const profiles = [local[CLOUD_KEY.profile], remote.keys[CLOUD_KEY.profile]] as const;
    plan.conflict = campaignWinner(profiles[0], profiles[1], input.dirtyAt, remote.updatedAt);
    for (const key of CAMPAIGN_KEYS) {
      if (same(key)) continue;
      const mine = local[key] ?? '';
      const theirs = remote.keys[key];
      if (plan.conflict.winner === 'local') {
        // The pair goes up together, so the server never holds one device's profile with the other's mission.
        dirty.add(key);
        if (theirs !== undefined && theirs !== '') plan.backup[key] = theirs;
      } else {
        if (mine !== '') plan.backup[key] = mine;
        // A suspended mission the winning profile knows nothing about must not survive it.
        plan.take[key] = theirs ?? '';
        plan.agreed[key] = fingerprint(theirs ?? '');
        dirty.delete(key);
      }
    }
  }

  plan.dirty = CLOUD_KEYS.filter((key) => dirty.has(key));
  return plan;
}
